import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, LockKeyhole, LogOut } from "lucide-react";
import { toast } from "sonner";
import { portalApi, portalError, isGone } from "@/lib/portalApi";
import { PortalConsentCard } from "@/components/PortalConsentCard";

const SRC = { self_service: "da te (Centro Preferenze)", admin: "dal mittente", contact: "da te", import: "importazione", import_l3: "importazione", legacy_unico: "consenso iniziale", form: "modulo", none: "—" };

function Shell({ children, title, subtitle }) {
  return (
    <main className="min-h-screen bg-[#F6F3EE] text-stone-900" data-testid="portal-page">
      <div className="mx-auto w-full max-w-xl px-4 py-8 sm:py-12 space-y-6">
        <header className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Centro Preferenze</p>
          <h1 className="text-2xl sm:text-3xl font-semibold" data-testid="portal-title">{title}</h1>
          {subtitle && <p className="text-sm text-stone-600" data-testid="portal-subtitle">{subtitle}</p>}
        </header>
        {children}
        <footer className="pt-6 text-[11px] text-stone-500">Le tue scelte sono registrate con data, ora e versione dell'informativa. Nessun dato viene mostrato prima della verifica della tua identità.</footer>
      </div>
    </main>);
}

function Gone() {
  return (
    <Shell title="Link non più valido" subtitle="Questo link è scaduto, è già stato usato o è stato disattivato.">
      <div className="rounded-2xl border border-stone-200 bg-white p-5 text-sm text-stone-700" data-testid="portal-gone">Richiedi un nuovo link al mittente delle comunicazioni. Puoi sempre rispondere <b>STOP</b> a un messaggio per non ricevere più marketing.</div>
    </Shell>);
}

function OtpStep({ token, ch, onVerified }) {
  const [sent, setSent] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const chans = ch.otp_channels || [];
  const [channel, setChannel] = useState(ch.otp_default || chans[0] || null);
  const send = async () => {
    setBusy(true);
    try { const r = await portalApi.post(`/${token}/otp`, channel ? { channel } : {}); setSent(r.data); toast.success("Codice inviato"); } catch (e) { toast.error(portalError(e)); } finally { setBusy(false); }
  };
  const verify = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await portalApi.post(`/${token}/verify`, { code }); toast.dismiss(); onVerified(); } catch (e2) { if (isGone(e2)) onVerified("gone"); else toast.error(portalError(e2)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm space-y-4" data-testid="portal-otp-step">
      <div className="flex items-center gap-3"><LockKeyhole className="w-5 h-5 text-stone-700" /><p className="text-sm text-stone-700">Per proteggere le tue preferenze verifichiamo che il numero <b data-testid="portal-phone-masked">{ch.phone_masked}</b> sia tuo.</p></div>
      {!sent ? (<>
        {chans.length > 1 && <div className="flex gap-2" data-testid="portal-otp-channel-choice">{chans.map((c) => <button key={c} type="button" onClick={() => setChannel(c)} data-testid={`portal-otp-channel-${c}`} className={`flex-1 rounded-full border px-3 py-2 text-sm ${channel === c ? "border-stone-900 bg-stone-900 text-white" : "border-stone-300 text-stone-700 hover:bg-stone-100"}`}>{c === "sms" ? "SMS" : "WhatsApp"}</button>)}</div>}
        <button onClick={send} disabled={busy} className="w-full rounded-full bg-stone-900 px-4 py-3 text-sm font-semibold text-white hover:bg-stone-700 disabled:opacity-50" data-testid="portal-otp-send-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : `Inviami un codice di verifica${channel ? ` via ${channel === "sms" ? "SMS" : "WhatsApp"}` : ""}`}
        </button></>
      ) : (
        <form onSubmit={verify} className="space-y-3">
          <p className="text-xs text-stone-500" data-testid="portal-otp-sent-info">Codice inviato via {sent.channel === "sms" ? "SMS" : "WhatsApp"} · valido {Math.round(sent.expires_in_s / 60)} minuti · {sent.remaining} nuovi invii disponibili{sent.delivery === "uncertain" ? " · consegna in corso: se non arriva, richiedi un nuovo codice" : ""}</p>
          <input inputMode="numeric" pattern="[0-9]*" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="Codice a 6 cifre" autoFocus
            className="w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-center text-2xl tracking-[0.5em] font-mono placeholder:text-base placeholder:tracking-normal focus:outline-none focus:ring-2 focus:ring-stone-900" data-testid="portal-otp-code-input" />
          <div className="flex gap-2">
            <button type="button" onClick={send} disabled={busy || sent.remaining <= 0} className="rounded-full border border-stone-300 px-4 py-2 text-sm text-stone-700 hover:bg-stone-100 disabled:opacity-40" data-testid="portal-otp-resend-btn">Nuovo codice</button>
            <button type="submit" disabled={busy || code.length !== 6} className="flex-1 rounded-full bg-stone-900 px-4 py-2 text-sm font-semibold text-white hover:bg-stone-700 disabled:opacity-40" data-testid="portal-otp-verify-btn">Verifica</button>
          </div>
        </form>)}
    </div>);
}

export default function PreferencePortal() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | gone | otp | manage
  const [ch, setCh] = useState(null);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const loadMe = useCallback(() => portalApi.get("/me").then((r) => { setMe(r.data); setState("manage"); }).catch(() => setState("otp")), []);
  useEffect(() => {
    portalApi.get(`/${token}/challenge`).then((r) => { setCh(r.data); setState("otp"); }).catch((e) => {
      if (isGone(e)) portalApi.get("/me").then((r) => { setMe(r.data); setState("manage"); }).catch(() => setState("gone"));
      else { toast.error(portalError(e)); setState("gone"); }
    });
  }, [token]);
  const change = async (channel, value, extra) => {
    setBusy(true);
    try {
      await portalApi.patch("/consents", { channel, value, rev: me.rev, ...extra });
      toast.success(value ? "Consenso registrato" : "Revoca registrata: non riceverai più marketing su questo canale");
      await loadMe();
    } catch (e) { toast.error(portalError(e)); if (e?.response?.status === 401) setState("otp"); else if (e?.response?.status === 409 && e?.response?.data?.detail?.code === "conflict") await loadMe(); throw e; } finally { setBusy(false); }
  };
  const pref = async (channel) => {
    try { await portalApi.patch("/preference", { channel }); toast.success("Preferenza aggiornata (non modifica i consensi)"); await loadMe(); } catch (e) { toast.error(portalError(e)); }
  };
  const notMe = async () => { try { await portalApi.post(`/${token}/not-me`); toast.success("Link disattivato"); } catch { /* già non valido */ } setState("gone"); };
  const logout = async () => { await portalApi.post("/logout").catch(() => {}); setMe(null); setState("gone"); };

  if (state === "loading") return <Shell title="Caricamento…"><Loader2 className="w-6 h-6 animate-spin text-stone-500" /></Shell>;
  if (state === "gone") return <Gone />;
  if (state === "otp") return (
    <Shell title={`Le tue preferenze con ${ch?.tenant_name || "il mittente"}`} subtitle="Scegli tu, canale per canale, se ricevere comunicazioni di marketing.">
      <OtpStep token={token} ch={ch || { phone_masked: "" }} onVerified={(g) => (g === "gone" ? setState("gone") : loadMe())} />
      <button onClick={notMe} className="text-xs text-stone-500 underline underline-offset-4 hover:text-stone-800" data-testid="portal-not-me-btn">Non sono io: disattiva questo link</button>
    </Shell>);
  return (
    <Shell title={`Le tue preferenze con ${me.tenant_name}`} subtitle={`Numero verificato ${me.phone_masked}. Ogni scelta è immediata.`}>
      <div className="space-y-4">
        {["whatsapp", "sms"].map((c) => <PortalConsentCard key={c} channel={c} data={me.channels[c]} privacy={me.privacy} busy={busy} onChange={change} />)}
        <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm space-y-3" data-testid="portal-preference">
          <h2 className="text-lg font-semibold">Canale preferito</h2>
          <p className="text-xs text-stone-500">Indica dove preferisci essere contattato. Non è un consenso: il marketing arriva solo sui canali per cui hai dato il consenso.</p>
          <div className="flex gap-2">
            {["whatsapp", "sms"].map((c) => (
              <button key={c} onClick={() => pref(c)} data-testid={`portal-preference-${c}`}
                className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${me.preference === c ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-700 hover:bg-stone-100"}`}>
                {c === "sms" ? "SMS" : "WhatsApp"}{me.preference === c ? " · preferito" : ""}
              </button>))}
          </div>
        </section>
        <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm space-y-2" data-testid="portal-history">
          <h2 className="text-lg font-semibold">Storico delle tue scelte</h2>
          {me.history.length === 0 ? <p className="text-sm text-stone-500" data-testid="portal-history-empty">Nessuna modifica registrata.</p> : (
            <ul className="divide-y divide-stone-100 text-sm">
              {me.history.map((h, i) => (
                <li key={i} className="py-2 flex flex-wrap justify-between gap-2" data-testid="portal-history-row">
                  <span className="text-stone-800">{h.channel === "sms" ? "SMS" : "WhatsApp"}: {String(h.from) === "true" ? "consenso" : String(h.from) === "false" ? "nessun consenso" : "non documentato"} → <b>{h.to === true ? "consenso" : h.to === false ? "nessun consenso" : "non documentato"}</b></span>
                  <span className="text-xs text-stone-500">{new Date(h.ts).toLocaleString("it-IT")} · {SRC[h.source] || h.source}</span>
                </li>))}
            </ul>)}
        </section>
        <div className="flex items-center justify-between pt-2">
          <button onClick={logout} className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900" data-testid="portal-logout-btn"><LogOut className="w-4 h-4" /> Esci</button>
          <span className="text-[11px] text-stone-500">Sessione fino alle {new Date(me.session_expires_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>
        </div>
      </div>
    </Shell>);
}
